import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';
const T = {
  crimson:    '#B01030',
  crimsonDk:  '#8A0D25',
  crimsonLt:  '#FCF0F2',
  crimsonMid: '#E8C0C8',
  slate900:   '#0D1B2A',
  slate700:   '#2C3E50',
  slate500:   '#5A6A7A',
  slate300:   '#A8B8C8',
  slate100:   '#EEF2F6',
  slate50:    '#F7F9FB',
  white:      '#FFFFFF',
  green:      '#0D8A5E',
  greenBg:    '#E8FAF4',
  amber:      '#C07A10',
  amberBg:    '#FDF6E8',
  orange:     '#C05818',
  orangeBg:   '#FDF0E8',
  red:        '#C01828',
  redBg:      '#FDE8EA',
  muted:      '#8A9BAC',
  mutedBg:    '#F0F4F8',
  border:     '#E2E8EF',
  shadow:     'rgba(13,27,42,0.07)',
};
// StatusChip Styled Component
const StatusChip = styled(Box)(({ theme, status }) => {

const colorMap = {
  all:            { color: T.crimson, bg: T.crimsonLt, border: T.crimsonMid },
  scheduled:      { color: T.green,  bg: T.greenBg,   border: '#A7E8D0'    },
  'no-answer':    { color: T.amber,  bg: T.amberBg,   border: '#F0D090'    },
  'wrong-number': { color: T.orange, bg: T.orangeBg,  border: '#F0C0A0'    },
  refused:        { color: T.red,    bg: T.redBg,     border: '#F0A0A8'    },
  'no-status':    { color: T.muted,  bg: T.mutedBg,   border: '#C8D8E8'    },
};

  const colors = colorMap[status] || colorMap['default'];

  return {
    backgroundColor: colors.background,
    color: colors.text,
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    display: 'inline-block',
    width: 'fit-content',
  };
});

const PatientDetailModal = ({ open, onClose, patient }) => {
  if (!patient) return null;

  const formatCreationDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '—';
    }
  };

  const readOnlyField = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      fontSize: '0.875rem',
      backgroundColor: T.slate50,
      '& fieldset': { borderColor: T.border },
    },
    '& input': { color: T.slate700 },
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      
      {/* Header */}
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${T.crimson}, ${T.crimsonDk})`,
        color: T.white,
        py: 2.25,
        px: 3,
        fontWeight: 700,
        fontSize: '0.95rem',
      }}>
        Patient Details
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 2 , mt: 2  }}>

          <TextField
            label="Full Name"
            fullWidth
            value={`${patient.firstName || ''} ${patient.lastName || ''}`}
            InputProps={{ readOnly: true }}
            sx={{ ...readOnlyField, gridColumn: 'span 2' }}
          />

          <TextField
            label="Email"
            fullWidth
            value={patient.email || '—'}
            InputProps={{ readOnly: true }}
            sx={{ ...readOnlyField, gridColumn: 'span 2' }}
          />

          <TextField
            label="Phone"
            fullWidth
            value={patient.phoneNumber || '—'}
            InputProps={{ readOnly: true }}
            sx={readOnlyField}
          />

          <TextField
            label="Insurance"
            fullWidth
            value={patient.hasInsurance || 'No'}
            InputProps={{ readOnly: true }}
            sx={readOnlyField}
          />

          {/* NEW FIELD */}
          <TextField
            label="Doctor Name"
            fullWidth
            value={patient.doctorName || '—'}
            InputProps={{ readOnly: true }}
            sx={readOnlyField}
          />

          {/* NEW FIELD */}
          <TextField
            label="Facility"
            fullWidth
            value={patient.facility || '—'}
            InputProps={{ readOnly: true }}
            sx={readOnlyField}
          />

          <TextField
            label="Date of Birth"
            fullWidth
            value={patient.dob ? new Date(patient.dob).toLocaleDateString('en-US') : '—'}
            InputProps={{ readOnly: true }}
            sx={readOnlyField}
          />

          <TextField
            label="CIN Number"
            fullWidth
            value={patient.cinNumber || '—'}
            InputProps={{ readOnly: true }}
            sx={readOnlyField}
          />

          {/* Status */}
          <Box sx={{ gridColumn: 'span 2' }}>
            <Typography sx={{
              fontSize: '0.68rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.07em',
              color: T.muted,
              mb: 0.5
            }}>
              Status
            </Typography>

            <StatusChip status={patient.status}>
              {patient.status?.replace('-', ' ') || 'No Status'}
            </StatusChip>
          </Box>

          <TextField
            label="Comment"
            fullWidth
            multiline
            rows={3}
            value={patient.comment || '—'}
            InputProps={{ readOnly: true }}
            sx={{ ...readOnlyField, gridColumn: 'span 2' }}
          />

          <TextField
            label="Creation Date"
            fullWidth
            value={formatCreationDate(patient.createdAt)}
            InputProps={{ readOnly: true }}
            sx={{ ...readOnlyField, gridColumn: 'span 2' }}
          />

        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{
        px: 3,
        py: 2,
        borderTop: `1px solid ${T.border}`
      }}>
        <Button
          onClick={onClose}
          sx={{
            color: T.slate500,
            borderRadius: 8,
            fontWeight: 500
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDetailModal;